import React, { useContext, useMemo } from 'react';
import {
  Box,
  Chip,
  Grid,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import RouterIcon from '@mui/icons-material/Router';
import AnalyticsIcon from '@mui/icons-material/Analytics';
import StorageIcon from '@mui/icons-material/Storage';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import CategoryIcon from '@mui/icons-material/Category';
import MemoryIcon from '@mui/icons-material/Memory';
import SyncIcon from '@mui/icons-material/Sync';
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';

import { TimezoneContext } from '../../common/TimezoneContext/TimezoneContext';
import { DetailField, DetailSection, StatusChip } from '../../WiviInventory/components';
import { WiviInventoryDetailContent } from '../../WiviInventory/WiviInventoryDetailContent';
import { getDeviceInsightSummary } from './deviceInsights';
import SignalStrengthIndicator from './SignalStrengthIndicator';

const hasDisplayValue = (value) => value !== null && value !== undefined && value !== '';

const renderValue = (value, fallback = 'Not available') => (hasDisplayValue(value) ? value : fallback);
const renderDateTimeValue = (value, formatDateTime) => value ? formatDateTime(value) : 'Not available';
const joinDisplayValues = (values, separator = ' / ') => {
  const parts = values.filter(hasDisplayValue).map(String);
  return parts.length ? parts.join(separator) : '';
};
const formatRegionLabel = (region) => {
  const parts = [region?.code, region?.name].filter(hasDisplayValue).map(String);
  return parts.length ? parts.join(' - ') : 'Not assigned';
};

export const DeviceInsightsDetailPanel = ({ device }) => {
  const { formatDateTime } = useContext(TimezoneContext);

  const {
    vin,
    inventory,
    logger,
  } = useMemo(() => getDeviceInsightSummary(device), [device]);

  const userLabel = renderValue(device?.user?.display_name || device?.user?.email, 'Unassigned');
  const regionLabel = formatRegionLabel(device?.region);
  const hasWiviDetail = Boolean(inventory.wiviDetail);

  return (
    <Box className="device-insights-panel">
      <Paper variant="outlined" className="device-insights-summary-card">
        <Box className="device-insights-summary-header">
          <Box>
            <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{ mt: 0 }}>
              {hasDisplayValue(vin) ? (
                <Chip icon={<DirectionsCarIcon />} label={`VIN: ${vin}`} color="primary" variant="filled" />
              ) : null}
              {hasDisplayValue(device?.mac_addr) ? (
                <Chip icon={<RouterIcon />} label={`MAC: ${device.mac_addr}`} variant="outlined" />
              ) : null}
              {hasDisplayValue(device?.pi) ? (
                <Chip icon={<CategoryIcon />} label={`PI: ${device.pi}`} variant="outlined" />
              ) : null}
              {hasDisplayValue(device?.device_type) ? (
                <Chip icon={<MemoryIcon />} label={`Type: ${device.device_type}`} variant="outlined" />
              ) : null}
            </Stack>
          </Box>

          <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" className="device-insights-summary-stats">
            <Chip icon={<AnalyticsIcon />} label={`${logger.totalLogs ?? 0} total logs`} color="primary" variant="outlined" />
            <Chip icon={<SyncIcon />} label={`Inventory sync ${inventory.syncedAt ? 'available' : 'missing'}`} color="secondary" variant="outlined" />
            {inventory.snapshotId ? (
              <Chip icon={<Inventory2OutlinedIcon />} label={`Snapshot ${inventory.snapshotId}`} variant="outlined" />
            ) : null}
          </Stack>
        </Box>
      </Paper>

      <Box className="device-insights-sections">
        <Paper variant="outlined" className="device-insights-section-card">
          <DetailSection title="Device Record" icon={<RouterIcon />}>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Stack spacing={0}>
                  <DetailField label="Device ID" value={device?.id} />
                  <DetailField label="VIN" value={vin} />
                  <DetailField label="MAC Address" value={device?.mac_addr} />
                  <DetailField label="PI" value={device?.pi} />
                  <DetailField label="Device Type" value={device?.device_type} />
                  <DetailField label="IP Address" value={device?.ip} />
                  <DetailField label="Last Activity" value={formatDateTime(device?.last_activity)} />
                </Stack>
              </Grid>
              <Grid item xs={12} md={6}>
                <Stack spacing={0}>
                  <DetailField label="User" value={userLabel} />
                  <DetailField label="Region" value={regionLabel} />
                  <DetailField label="Registration Status" value={device?.device_registration_status} />
                  <DetailField label="Registration Expiry" value={formatDateTime(device?.device_registration_expiry_date)} />
                  <DetailField label="Tailscale Status" value={device?.tailscale_approval_status} />
                  <DetailField label="Tailscale Expiry" value={formatDateTime(device?.tailscale_approval_expiry_date)} />
                </Stack>
              </Grid>
            </Grid>

            <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{ mt: 2 }}>
              <StatusChip value={device?.is_active} trueLabel="Active" falseLabel="Inactive" />
              <StatusChip value={!device?.is_lost} trueLabel="Not Lost" falseLabel="Lost" />
              <StatusChip value={!device?.is_broken} trueLabel="Working" falseLabel="Broken" />
            </Stack>
          </DetailSection>
        </Paper>

        <Paper variant="outlined" className="device-insights-section-card">
          <DetailSection title="Logger Summary" icon={<AnalyticsIcon />}>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Stack spacing={0}>
                  <DetailField label="Total Logs" value={logger.totalLogs} />
                  <DetailField label="First Seen" value={renderDateTimeValue(logger.firstSeen, formatDateTime)} />
                  <DetailField label="Last Seen" value={renderDateTimeValue(logger.lastSeen, formatDateTime)} />
                </Stack>
              </Grid>
              <Grid item xs={12} md={6}>
                <Stack spacing={0}>
                  <DetailField label="Logger Updated At" value={renderDateTimeValue(logger.updatedAt, formatDateTime)} />
                  <DetailField label="Freshest Activity" value={logger.lastSeen ? 'Logger activity present' : 'No logger activity yet'} />
                </Stack>
              </Grid>
            </Grid>
          </DetailSection>
        </Paper>

        <Paper variant="outlined" className="device-insights-section-card">
          <DetailSection title="Inventory Summary" icon={<StorageIcon />}>
            <Grid container spacing={3}>
              {!hasWiviDetail ? (
                <Grid item xs={12} md={6}>
                  <Stack spacing={0}>
                    <DetailField label="Display Name" value={inventory.displayName} />
                    <DetailField label="Remote Device Name" value={inventory.remoteDeviceName} />
                    <DetailField label="Remote Device Type" value={inventory.remoteDeviceType} />
                    <DetailField label="Client Version" value={inventory.clientVersion} />
                    <DetailField label="Make / Model" value={joinDisplayValues([inventory.make, inventory.model])} />
                    <DetailField label="Year" value={inventory.year} />
                    <DetailField label="Fleet" value={inventory.remoteFleetName} />
                    <DetailField label="Fleet ID" value={inventory.remoteFleetId} />
                    <DetailField label="Organization" value={inventory.remoteOrganizationName} />
                    <DetailField label="Organization ID" value={inventory.remoteOrganizationId} />
                    <DetailField label="Network Type" value={inventory.networkType} />
                    <DetailField label="Inventory Last Updated" value={renderDateTimeValue(inventory.lastUpdated, formatDateTime)} />
                    <DetailField label="Inventory Synced At" value={renderDateTimeValue(inventory.syncedAt, formatDateTime)} />
                  </Stack>
                </Grid>
              ) : null}
              <Grid item xs={12} md={hasWiviDetail ? 12 : 6}>
                <Stack spacing={2} sx={{ pt: hasWiviDetail ? 0 : 1 }}>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Signal Strength
                    </Typography>
                    <Box sx={{ mt: 0.75 }}>
                      <SignalStrengthIndicator
                        value={inventory.signalBars}
                        networkType={inventory.networkType}
                        deviceType={inventory.signalDeviceType}
                      />
                    </Box>
                  </Box>
                  <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                    <StatusChip value={inventory.online} trueLabel="Online" falseLabel="Offline" />
                    <StatusChip value={inventory.logging} trueLabel="Logging" falseLabel="Not Logging" />
                    <StatusChip value={inventory.running} trueLabel="Running" falseLabel="Stopped" />
                    <Chip icon={<AccessTimeIcon />} label={`Synced: ${renderDateTimeValue(inventory.syncedAt, formatDateTime)}`} variant="outlined" />
                    {inventory.wiviMatchStatus ? (
                      <Chip label={`Wivi match: ${inventory.wiviMatchStatus}`} variant="outlined" />
                    ) : null}
                    {inventory.loggerMatchStatus ? (
                      <Chip label={`Logger match: ${inventory.loggerMatchStatus}`} variant="outlined" />
                    ) : null}
                  </Stack>
                  {inventory.wiviMatchReason ? (
                    <Typography variant="body2" color="text.secondary">
                      {inventory.wiviMatchReason
                        .replace(/_/g, ' ')
                        .replace(/\b\w/g, (c) => c.toUpperCase())}
                    </Typography>
                  ) : null}
                </Stack>
              </Grid>
            </Grid>
          </DetailSection>
        </Paper>

        {inventory.wiviDetail ? (
          <Paper variant="outlined" className="device-insights-section-card">
            <DetailSection title="Wivi Detail" icon={<Inventory2OutlinedIcon />}>
              <WiviInventoryDetailContent
                snapshotId={inventory.snapshotId}
                fallbackData={inventory.wiviDetail}
                enableFetch={false}
              />
            </DetailSection>
          </Paper>
        ) : null}
      </Box>
    </Box>
  );
};

export default DeviceInsightsDetailPanel;